import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * Represents the result of applying a single ADF tool call command to the editor.
 */
export type AdfToolCallResult = {
	/** Whether this tool call was applied successfully */
	apply_status: 'success' | 'fail';
	/** Categorized error type matching GASv3 analytics categories, null on success */
	error_type: string | null;
	/** The target node localId (if applicable) */
	local_id: string | null;
	/** Human-readable error message, null on success */
	message: string | null;
	/** The raw tool call command object as received from the stream, for debugging/reproduction */
	raw_command: Record<string, unknown> | null;
	/** When this tool call was processed */
	timestamp: number;
	/** The streaming command type, e.g. 'replaceNode', 'insertNodeAfter' */
	tool_call: string;
};

/**
 * Summary statistics for a streaming session.
 */
export type AdfToolCallSessionSummary = {
	/** Count of each error type encountered */
	error_breakdown: Record<string, number>;
	/** Number of failed tool calls */
	fail: number;
	/** Number of successful tool calls */
	success: number;
	/** Success rate as a percentage (0–100) */
	success_rate: number;
	/** Total number of tool calls in this session */
	total: number;
};

/**
 * Represents a single ADF streaming session (one Rovo chat interaction that edits a page).
 */
export type AdfToolCallSession = {
	/** When the session ended (null if still streaming) */
	ended_at: number | null;
	/** All tool call results in this session */
	results: AdfToolCallResult[];
	/** When the session started */
	started_at: number;
	/** Current session status */
	status: 'streaming' | 'completed' | 'errored' | 'aborted' | 'timeout';
	/** The type of stream ('update') */
	stream_type: string;
	/** Computed summary statistics */
	summary: AdfToolCallSessionSummary;
	/** The backend trace ID linking to the Rovo chat request */
	trace_id: string | null;
};

/**
 * The public API shape exposed on window.__adfToolCallsDebugger__.
 */
export type AdfToolCallsDebuggerAPI = {
	/** Clear all tracked data */
	clear: () => void;
	/** Get all errors, optionally filtered by trace ID */
	getErrors: (traceId?: string) => AdfToolCallResult[];
	/** Get a specific session by trace ID */
	getSession: (traceId: string) => AdfToolCallSession | undefined;
	/** Get all tracked sessions */
	getSessions: () => AdfToolCallSession[];
	/** Get aggregate summary across all tracked sessions */
	getSummary: () => {
		error_breakdown: Record<string, number>;
		overall_success_rate: number;
		sessions_by_status: Record<string, number>;
		total_sessions: number;
		total_tool_calls: number;
	};
};

declare global {
	interface Window {
		__adfToolCallsDebugger__: AdfToolCallsDebuggerAPI | undefined;
	}
}

/**
 * Internal session data used by the debugger before computing the public summary.
 */
type InternalSession = {
	ended_at: number | null;
	results: AdfToolCallResult[];
	started_at: number;
	status: 'streaming' | 'completed' | 'errored' | 'aborted' | 'timeout';
	stream_type: string;
	trace_id: string | null;
};

/**
 * AdfToolCallsDebugger provides a window-level debugging API for inspecting
 * ADF streaming tool call results in real time from the browser console.
 *
 * It maintains a time-based LRU cache of streaming sessions and exposes
 * methods to query errors, sessions, and aggregate success rates.
 *
 * Usage from browser console:
 *   window.__adfToolCallsDebugger__.getErrors()
 *   window.__adfToolCallsDebugger__.getSession('trace-id-here')
 *   window.__adfToolCallsDebugger__.getSummary()
 *
 * Gated behind the 'platform_editor_ai_adf_tool_calls_debugger' feature flag.
 */
export class AdfToolCallsDebugger {
	private static instance: AdfToolCallsDebugger | null = null;

	private sessions: Map<string, InternalSession> = new Map();
	private sessionOrder: string[] = [];

	/** Maximum number of sessions to retain */
	private readonly maxSessions: number;
	/** Maximum age of a session in milliseconds before eviction */
	private readonly maxAgeMsec: number;

	/** Key used for sessions that have no trace ID */
	private anonymousCounter = 0;

	private constructor(maxSessions = 50, maxAgeMsec = 30 * 60 * 1000) {
		this.maxSessions = maxSessions;
		this.maxAgeMsec = maxAgeMsec;
	}

	/**
	 * Returns the singleton instance of the debugger if the feature flag is enabled.
	 * Also attaches the public API to `window.__adfToolCallsDebugger__`.
	 */
	static getInstance(): AdfToolCallsDebugger | null {
		if (!fg('platform_editor_ai_adf_tool_calls_debugger')) {
			return null;
		}

		if (!AdfToolCallsDebugger.instance) {
			AdfToolCallsDebugger.instance = new AdfToolCallsDebugger();
			AdfToolCallsDebugger.instance.attachToWindow();
		}

		return AdfToolCallsDebugger.instance;
	}

	/**
	 * Attaches the public debugging API to the window object.
	 */
	private attachToWindow(): void {
		try {
			const target = window.top ?? window;
			target.__adfToolCallsDebugger__ = {
				getErrors: (traceId?: string) => this.getErrors(traceId),
				getSessions: () => this.getSessions(),
				getSession: (traceId: string) => this.getSession(traceId),
				getSummary: () => this.getSummary(),
				clear: () => this.clear(),
			};
		} catch {
			// Ignore cross-origin frame access errors
		}
	}

	private getSessionKey(traceId: string | null | undefined): string {
		if (traceId) {
			return traceId;
		}
		return `__anonymous_${++this.anonymousCounter}__`;
	}

	private evictExpired(): void {
		const now = Date.now();
		const keysToRemove: string[] = [];

		for (const key of this.sessionOrder) {
			const session = this.sessions.get(key);
			if (session && now - session.started_at > this.maxAgeMsec) {
				keysToRemove.push(key);
			}
		}

		for (const key of keysToRemove) {
			this.sessions.delete(key);
			this.sessionOrder = this.sessionOrder.filter((k) => k !== key);
		}
	}

	private evictOldest(): void {
		while (this.sessionOrder.length > this.maxSessions) {
			const oldest = this.sessionOrder.shift();
			if (oldest) {
				this.sessions.delete(oldest);
			}
		}
	}

	private computeSummary(session: InternalSession): AdfToolCallSessionSummary {
		const results = session.results;
		const total = results.length;
		const fail = results.filter((r) => r.apply_status === 'fail').length;
		const success = total - fail;
		const successRate = total > 0 ? Math.round((success / total) * 10000) / 100 : 100;

		const errorBreakdown: Record<string, number> = {};
		for (const r of results) {
			if (r.error_type) {
				errorBreakdown[r.error_type] = (errorBreakdown[r.error_type] || 0) + 1;
			}
		}

		return {
			total,
			success,
			fail,
			success_rate: successRate,
			error_breakdown: errorBreakdown,
		};
	}

	private toPublicSession(session: InternalSession): AdfToolCallSession {
		return {
			...session,
			results: [...session.results],
			summary: this.computeSummary(session),
		};
	}

	// --- Public recording API (called by the streaming infrastructure) ---

	/** The key of the current active session, used by the command processor */
	private currentSessionKey: string | null = null;

	/**
	 * Records the start of a new streaming session.
	 */
	startSession(traceId: string | null | undefined, streamType: string = 'update'): void {
		this.evictExpired();

		const key = this.getSessionKey(traceId);
		this.currentSessionKey = key;

		const session: InternalSession = {
			trace_id: traceId ?? null,
			stream_type: streamType,
			started_at: Date.now(),
			ended_at: null,
			status: 'streaming',
			results: [],
		};

		this.sessions.set(key, session);
		this.sessionOrder.push(key);
		this.evictOldest();
	}

	/**
	 * Records a successful tool call in the current session.
	 */
	recordToolCallSuccess(
		toolCall: string,
		localId: string | null,
		rawCommand?: Record<string, unknown> | null,
	): void {
		const session = this.getCurrentSession();
		if (!session) {
			return;
		}

		session.results.push({
			apply_status: 'success',
			error_type: null,
			tool_call: toolCall,
			local_id: localId,
			message: null,
			raw_command: rawCommand ?? null,
			timestamp: Date.now(),
		});
	}

	/**
	 * Records a failed tool call in the current session.
	 */
	recordToolCallError(
		toolCall: string,
		localId: string | null,
		errorType: string,
		message: string,
		rawCommand?: Record<string, unknown> | null,
	): void {
		const session = this.getCurrentSession();
		if (!session) {
			return;
		}

		session.results.push({
			apply_status: 'fail',
			error_type: errorType,
			tool_call: toolCall,
			local_id: localId,
			message,
			raw_command: rawCommand ?? null,
			timestamp: Date.now(),
		});
	}

	/**
	 * Records a session-level error (not tied to a specific tool call).
	 */
	recordSessionError(errorType: string, message: string): void {
		const session = this.getCurrentSession();
		if (!session) {
			return;
		}

		session.results.push({
			apply_status: 'fail',
			error_type: errorType,
			tool_call: 'session',
			local_id: null,
			message,
			raw_command: null,
			timestamp: Date.now(),
		});
	}

	/**
	 * Marks the current session as completed with the given status.
	 */
	endSession(status: 'completed' | 'errored' | 'aborted' | 'timeout' = 'completed'): void {
		const session = this.getCurrentSession();
		if (!session) {
			return;
		}

		session.ended_at = Date.now();
		session.status = status;
		this.currentSessionKey = null;
	}

	private getCurrentSession(): InternalSession | undefined {
		if (!this.currentSessionKey) {
			return undefined;
		}
		return this.sessions.get(this.currentSessionKey);
	}

	// --- Public query API (exposed on window) ---

	/**
	 * Returns all error results, optionally filtered by trace ID.
	 */
	getErrors(traceId?: string): AdfToolCallResult[] {
		this.evictExpired();

		if (traceId) {
			const session = this.sessions.get(traceId);
			if (!session) {
				return [];
			}
			return session.results.filter((r) => r.apply_status === 'fail');
		}

		const errors: AdfToolCallResult[] = [];
		for (const session of this.sessions.values()) {
			for (const result of session.results) {
				if (result.apply_status === 'fail') {
					errors.push(result);
				}
			}
		}
		return errors;
	}

	/**
	 * Returns all tracked sessions with computed summaries.
	 */
	getSessions(): AdfToolCallSession[] {
		this.evictExpired();
		return Array.from(this.sessions.values()).map((s) => this.toPublicSession(s));
	}

	/**
	 * Returns a single session by trace ID.
	 */
	getSession(traceId: string): AdfToolCallSession | undefined {
		this.evictExpired();
		const session = this.sessions.get(traceId);
		if (!session) {
			return undefined;
		}
		return this.toPublicSession(session);
	}

	/**
	 * Returns aggregate summary statistics across all tracked sessions.
	 */
	getSummary(): {
		error_breakdown: Record<string, number>;
		overall_success_rate: number;
		sessions_by_status: Record<string, number>;
		total_sessions: number;
		total_tool_calls: number;
	} {
		this.evictExpired();

		let totalToolCalls = 0;
		let totalSuccess = 0;
		const errorBreakdown: Record<string, number> = {};
		const sessionsByStatus: Record<string, number> = {};

		for (const session of this.sessions.values()) {
			sessionsByStatus[session.status] = (sessionsByStatus[session.status] || 0) + 1;

			for (const result of session.results) {
				totalToolCalls++;
				if (result.apply_status === 'success') {
					totalSuccess++;
				}
				if (result.error_type) {
					errorBreakdown[result.error_type] = (errorBreakdown[result.error_type] || 0) + 1;
				}
			}
		}

		return {
			total_sessions: this.sessions.size,
			total_tool_calls: totalToolCalls,
			overall_success_rate:
				totalToolCalls > 0 ? Math.round((totalSuccess / totalToolCalls) * 10000) / 100 : 100,
			error_breakdown: errorBreakdown,
			sessions_by_status: sessionsByStatus,
		};
	}

	/**
	 * Clears all tracked session data.
	 */
	clear(): void {
		this.sessions.clear();
		this.sessionOrder = [];
		this.currentSessionKey = null;
	}

	/**
	 * Resets the singleton instance. Only intended for use in tests.
	 * @internal
	 */
	static resetForTesting(): void {
		AdfToolCallsDebugger.instance = null;
	}
}
